// theme.js — Groundwork FC design tokens
//
// Replaces the ad-hoc T / s objects used throughout Groundwork DB.
// Every value here has a defined job. Contrast ratios are measured
// against bg.surface and stated so they aren't quietly eroded later.

export const bg = {
  base:    '#0B0912',  // app background
  surface: '#12101C',  // panels, cards, sidebars
  raised:  '#1A1728',  // hover, inputs, nested blocks
  overlay: '#221E33',  // modals, dropdowns, progress tracks
};

export const line = {
  DEFAULT: '#262236',  // standard divider
  strong:  '#332E47',  // input borders, emphasised divider
};

// Contrast vs bg.surface shown in comments. AA body needs 4.5, large text 3.0.
export const tx = {
  primary:   '#F2F4FA',  // 17.1 — names, headings, values
  secondary: '#A9B4CC',  //  9.0 — body copy, descriptions
  muted:     '#7D8AA3',  //  5.4 — labels, metadata, timestamps
  faint:     '#5E6B85',  //  3.5 — disabled and placeholder ONLY, never content
};

export const accent = {
  lime:    '#C8F135',  // 14.4 — actionable or critical. Use sparingly.
  limeDim: '#9DBE2A',  //  8.8 — lime on light fills, secondary emphasis
  cyan:    '#4DD8E8',  // 11.0 — scouting, information, away team
  green:   '#3DD68C',  // 10.0 — positive change, good fit, healthy
  amber:   '#FFB020',  // 10.3 — warning, expiring, yellow card
  red:     '#FF4D6A',  //  5.8 — negative change, crisis, red card
  violet:  '#A78BFA',  //  6.9 — potential, youth, distant regions
  coral:   '#FF8065',  // warm energy — form, hot prospects, transfer activity
  gold:    '#F5C65D',  // honours, reputation, standout
};

// Translucent fills for badges and tinted rows
export const tint = {
  lime:   'rgba(200,241,53,.14)',
  cyan:   'rgba(77,216,232,.14)',
  green:  'rgba(61,214,140,.14)',
  amber:  'rgba(255,176,32,.14)',
  red:    'rgba(255,77,106,.14)',
  violet: 'rgba(167,139,250,.14)',
  coral:  'rgba(255,128,101,.14)',
  gold:   'rgba(245,198,93,.14)',
  limeGlow: 'rgba(200,241,53,.10)', // pitch glow, hero wash
};

export const font = {
  display: "'Chakra Petch', sans-serif",  // headings, labels, buttons
  body:    "'Inter', sans-serif",          // body copy, commentary
  data:    "'JetBrains Mono', monospace",  // every number in the game
};

// Type scale — size, weight, letterSpacing, lineHeight, intended family
export const type = {
  display:   { fontFamily: font.display, fontSize: 36, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.1 },
  h1:        { fontFamily: font.display, fontSize: 26, fontWeight: 700, letterSpacing: '0.01em',  lineHeight: 1.2 },
  h2:        { fontFamily: font.display, fontSize: 19, fontWeight: 700, letterSpacing: '0.03em',  lineHeight: 1.3, textTransform: 'uppercase' },
  h3:        { fontFamily: font.display, fontSize: 15, fontWeight: 600, letterSpacing: '0.04em',  lineHeight: 1.4 },
  eyebrow:   { fontFamily: font.display, fontSize: 11, fontWeight: 700, letterSpacing: '0.24em',  textTransform: 'uppercase' },
  body:      { fontFamily: font.body,    fontSize: 14, fontWeight: 400, lineHeight: 1.65 },
  small:     { fontFamily: font.body,    fontSize: 13, fontWeight: 400, lineHeight: 1.55 },
  label:     { fontFamily: font.display, fontSize: 12, fontWeight: 600, letterSpacing: '0.08em',  textTransform: 'uppercase' },
  dataLarge: { fontFamily: font.data,    fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1 },
  data:      { fontFamily: font.data,    fontSize: 15, fontWeight: 500 },
  dataSmall: { fontFamily: font.data,    fontSize: 12, fontWeight: 500 },
};

// 4px base unit — everything snaps to these
export const space = { s1: 4, s2: 8, s3: 12, s4: 16, s5: 24, s6: 32, s7: 48, s8: 64 };

export const radius = { sm: 3, md: 5, lg: 8 };

// ── Semantic helpers ────────────────────────────────────────────────────────

// Player rating 1–10
export const ratingColour = r =>
  r >= 8.0 ? accent.lime :
  r >= 7.0 ? accent.green :
  r >= 6.0 ? tx.secondary :
  r >= 5.0 ? accent.amber : accent.red;

// Attribute or CA value 1–100
export const attrColour = v =>
  v >= 80 ? accent.lime :
  v >= 60 ? accent.green :
  v >= 40 ? accent.amber : accent.red;

// Fatigue 0–100 (higher is worse)
export const fatigueColour = f =>
  f < 40 ? accent.green :
  f < 65 ? accent.amber :
  f < 85 ? '#FB923C' : accent.red;

// Club financial health band
export const healthColour = band => ({
  Healthy:   accent.green,
  Stable:    accent.lime,
  Tight:     accent.amber,
  Stretched: '#FB923C',
  Crisis:    accent.red,
}[band] || tx.muted);

// Scouting region tier
export const tierColour = tier => ({
  home:    accent.lime,
  near:    accent.green,
  europe:  accent.cyan,
  distant: accent.violet,
}[tier] || tx.muted);

// League table zones — marker colour plus row tint.
// Apply the marker as a 3px bar on the row's first cell; the tint alone
// is too subtle to scan at a glance.
export const zone = {
  promotion:  { marker: accent.green, bg: 'rgba(61,214,140,.13)',  hover: 'rgba(61,214,140,.19)'  },
  playoff:    { marker: accent.amber, bg: 'rgba(255,176,32,.11)',  hover: 'rgba(255,176,32,.17)'  },
  relegation: { marker: accent.red,   bg: 'rgba(255,77,106,.11)',  hover: 'rgba(255,77,106,.17)'  },
  none:       { marker: 'transparent', bg: 'transparent',          hover: bg.raised               },
};

// SVG rule: any shape with a visible border must use
// vector-effect="non-scaling-stroke" with an explicit px stroke-width.
// Without it, stroke weight scales with the viewBox and renders
// inconsistently as the container resizes.
export const CHART_STROKE = 2;      // bordered shapes: bars, rects
export const CHART_LINE   = 0.9;    // data lines, in viewBox units
export const CHART_GRID   = 0.5;    // grid lines, dashed '2 3'

const theme = { bg, line, tx, accent, tint, font, type, space, radius, zone };
export default theme;
